import { yupResolver } from '@hookform/resolvers/yup';
import CloseIcon from '@mui/icons-material/Close';
import {
  Alert,
  Box,
  Button,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import { styled } from '@mui/material/styles';
import React, { useEffect, useState } from 'react';
import type { Resolver } from 'react-hook-form';
import { Controller, useForm } from 'react-hook-form';
import * as Yup from 'yup';

import type { BulkUploadLeadsRequest } from '@/types/employment/BulkUploadLeadsRequest';

import Loader from './Loader';

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialogContent-root': {
    padding: theme.spacing(0),
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(1),
  },
}));

export interface DialogTitleProps {
  id: string;
  children?: React.ReactNode;
  onClose: () => void;
  isForm2290Template?: boolean;
  setIsBulkUploadTemplate?: (value: boolean) => void;
}
export interface Form2290TemplateDownloadProps {
  isForm2290Template: boolean;
  setIsForm2290Template: React.Dispatch<React.SetStateAction<boolean>>;
}

function BootstrapDialogTitle(props: DialogTitleProps) {
  const { children, onClose, ...other } = props;

  return (
    <DialogTitle sx={{ m: 0, p: 0 }} {...other}>
      {children}
      {onClose ? (
        <Tooltip title="Close" arrow>
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              p: 0,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </Tooltip>
      ) : null}
    </DialogTitle>
  );
}

const Form2290DownloadTemplatePopup = (
  props: Form2290TemplateDownloadProps
) => {
  const [isLoading, setIsLoading] = useState(false);
  const [openSnack, setSnackOpen] = useState(false);

  const handleSnackClose = () => {
    setSnackOpen(false);
  };

  const validationSchema = Yup.object().shape({
    UserName: Yup.string().required('Name is required'),
    EmailAddress: Yup.string()
      .required('Email Address is required')
      .email('Enter valid Email Address'),
  });

  const defaultValue: BulkUploadLeadsRequest = {
    EmailAddress: '',
    UserName: '',
    Category: '',
    Token: '',
  };

  const { handleSubmit, control, reset } = useForm<BulkUploadLeadsRequest>({
    mode: 'onChange',
    resolver: yupResolver(validationSchema) as unknown as Resolver<
      BulkUploadLeadsRequest,
      any
    >,
    defaultValues: defaultValue,
  });

  React.useEffect(() => {
    const loadScriptByURL = (id: any, url: any, callback: any) => {
      const isScriptExist = document.getElementById(id);

      if (!isScriptExist) {
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.src = url;
        script.id = id;
        // eslint-disable-next-line func-names
        script.onload = function () {
          if (callback) callback();
        };
        document.body.appendChild(script);
      }

      if (isScriptExist && callback) callback();
    };

    // load the script by passing the URL
    loadScriptByURL(
      'recaptcha-key',
      `https://www.google.com/recaptcha/api.js?render=${process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY}&badge=bottomleft`,
      // eslint-disable-next-line func-names
      function () {
        // console.log('Script loaded!');
      }
    );
  }, []);

  const downloadTemplate = () => {
    const link = document.createElement('a');
    link.href =
      '/assets/images/template/taxzerone_form-2290-full-service-template.xlsx';
    link.download = 'taxzerone_form-2290-full-service-template.xlsx';
    document.body.appendChild(link);
    link.click();
    setSnackOpen(true);
    document.body.removeChild(link);
  };

  const onSubmit = async (data: BulkUploadLeadsRequest) => {
    setIsLoading(true);
    window.grecaptcha.ready(() => {
      window.grecaptcha
        .execute(process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY, {
          action: 'submit',
        })
        .then(async (token: any) => {
          /* send data to the server */
          const postData: BulkUploadLeadsRequest = data;
          postData.Category = 'Form2290Template';
          postData.Token = token;
          const address = `/api/bulkuploadlead/`;
          const contactusResponse = await fetch(address, {
            method: 'POST',
            body: JSON.stringify(postData),
          });
          const contactusResponseData = await contactusResponse.json();
          if (contactusResponseData === true) {
            reset(defaultValue);
            // Trigger file download
            downloadTemplate();
          }
          setIsLoading(false);
        })
        .catch(() => {});
    });
  };

  const handleClose = () => {
    props.setIsForm2290Template(false);
  };

  useEffect(() => {
    reset(defaultValue);
  }, [props.isForm2290Template]);

  return (
    <BootstrapDialog
      onClose={handleClose}
      aria-labelledby="customized-dialog-title"
      open={props.isForm2290Template}
      className="holiday-theme"
      fullWidth
      maxWidth="md"
    >
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={openSnack}
        autoHideDuration={6000}
        onClose={handleSnackClose}
      >
        <Alert
          onClose={handleSnackClose}
          severity="success"
          sx={{
            width: '100%',
            backgroundColor: '#198754',
            color: '#fff',
            fontWeight: 600,
          }}
        >
          Form 2290 template downloaded successfully
        </Alert>
      </Snackbar>
      <Loader isLoading={isLoading} />
      <BootstrapDialogTitle
        id="customized-dialog-title"
        onClose={handleClose}
      />

      <DialogContent>
        <Stack
          direction={{ xs: 'column-reverse', sm: 'row' }}
          justifyContent="space-between"
          alignItems="stretch"
          spacing={0}
        >
          <Box
            component="form"
            autoComplete="off"
            onSubmit={handleSubmit(onSubmit)}
            sx={{
              width: '100%',
              p: 3,
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontWeight: 600,
                color: '#000000',
                fontSize: { md: 28, sm: 20, xs: 18 },
                mb: 2,
              }}
            >
              Download Our{' '}
              <span style={{ fontWeight: 700, color: '#1da94a' }}>
                Form 2290
              </span>{' '}
              Full Service Template Now!
            </Typography>
            <Typography
              variant="body1"
              sx={{ fontSize: 17, fontWeight: 500, mb: 2 }}
            />
            <Box sx={{ textAlign: 'center' }}>
              <Controller
                name="UserName"
                control={control}
                render={({
                  field: { onChange, value },
                  fieldState: { error },
                }) => (
                  <TextField
                    id="UserName"
                    name="UserName"
                    label="*Name"
                    color="secondary"
                    variant="outlined"
                    className={value ? 'prefilled' : 'mandatory'}
                    value={value}
                    onChange={onChange}
                    error={!!error}
                    helperText={error ? error.message : null}
                    inputProps={{
                      maxLength: 100,
                      maxHight: 100,
                    }}
                    fullWidth
                    sx={{ mb: 2 }}
                  />
                )}
              />
              <Controller
                name="EmailAddress"
                control={control}
                render={({
                  field: { onChange, value },
                  fieldState: { error },
                }) => (
                  <TextField
                    className={value ? 'prefilled' : 'mandatory'}
                    id="EmailAddress"
                    label="*Email Address"
                    variant="outlined"
                    autoComplete="nope"
                    color="secondary"
                    inputProps={{
                      maxLength: 100,
                    }}
                    value={value}
                    onChange={onChange}
                    error={!!error}
                    helperText={error ? error.message : null}
                    fullWidth
                    sx={{ mb: 2 }}
                  />
                )}
              />
              <Button
                variant="contained"
                size="large"
                type="submit"
                sx={{
                  textTransform: 'none',
                  boxShadow: 'none',
                  borderRadius: 4,
                  display: 'inline-block',
                  fontSize: { xs: 18, sm: 20, md: 22, lg: 24 },
                }}
              >
                Download Template
              </Button>
            </Box>
          </Box>
          <Box
            sx={{
              maxWidth: 300,
              width: '100%',
              minHeight: { xs: 339, sm: 380, md: 450 },
              minWidth: { xs: 200, sm: 339, md: 370 },
              display: { xs: 'none', sm: 'block' },
            }}
            className="popup-excel-img"
          />
        </Stack>
      </DialogContent>
    </BootstrapDialog>
  );
};

export default Form2290DownloadTemplatePopup;
